import React, { useState, useEffect } from 'react';
import { checkSessionTimeoutWarning, extendSessionActivity, updateGameActivity } from '../utils/sessionManagement';

const SessionWarning = ({ userId, sessionId, onSessionExpired }) => {
  const [warning, setWarning] = useState(null);
  const [isExtending, setIsExtending] = useState(false);

  useEffect(() => {
    const checkWarning = () => {
      const warningStatus = checkSessionTimeoutWarning(userId, sessionId);
      setWarning(warningStatus);
    };

    // Check immediately
    checkWarning();

    // Check every minute
    const interval = setInterval(checkWarning, 60 * 1000);

    return () => clearInterval(interval);
  }, [userId, sessionId]);

  const handleExtendSession = async () => {
    setIsExtending(true);
    try {
      // Update game activity if in game
      if (warning?.isGameSession) {
        await updateGameActivity(userId, sessionId);
      }
      
      // Extend session
      const success = await extendSessionActivity(userId, sessionId);
      if (success) {
        setWarning(null);
      }
    } catch (error) {
      console.error('Error extending session:', error);
    } finally {
      setIsExtending(false);
    }
  };

  const formatTime = (milliseconds) => {
    const minutes = Math.floor(milliseconds / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!warning?.shouldWarn) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg shadow-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-yellow-800">
              {warning.isGameSession ? 'Game Session Warning' : 'Session Timeout Warning'}
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                {warning.isGameSession ? (
                  <>
                    Your game session will expire in{' '}
                    <span className="font-mono font-bold">{formatTime(warning.timeRemaining)}</span>
                    {warning.userRole === 'host' && ' (Host sessions have extended timeouts)'}
                  </>
                ) : (
                  <>
                    Your session will expire in{' '}
                    <span className="font-mono font-bold">{formatTime(warning.timeRemaining)}</span>
                  </>
                )}
              </p>
              {warning.isGameSession && (
                <p className="mt-1 text-xs text-yellow-600">
                  ⚠️ Game sessions are protected from automatic logout to prevent data loss
                </p>
              )}
            </div>
            <div className="mt-3 flex space-x-2">
              <button
                onClick={handleExtendSession}
                disabled={isExtending}
                className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-400 text-white text-xs font-medium px-3 py-1.5 rounded-md transition-colors"
              >
                {isExtending ? 'Extending...' : 'Extend Session'}
              </button>
              {!warning.isGameSession && (
                <button
                  onClick={() => setWarning(null)}
                  className="text-yellow-600 hover:text-yellow-800 text-xs font-medium px-3 py-1.5 rounded-md transition-colors"
                >
                  Dismiss
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionWarning; 